import * as React from 'react';

import { Celula } from '@/componentes/celula';
import { FaixaEmpresa } from '@/componentes/faixa-empresa';
import { Passos } from '@/componentes/passos';
import { ETAPAS, type IdEtapa } from '@/etapas';
import { cn } from '@/lib/utils';
import { Wrap } from '@/layout/wrap';

/* Pagina — a tela inteira: casco, trilha do funil, cabeçalho da etapa e o
 * conteúdo.
 *
 * A ORDEM é fixa e não varia por tela: marca, onde estou na jornada, o que
 * esta tela decide (e de quem são os números), o conteúdo. Existe para que as
 * nove telas não reinventem nove cabeçalhos diferentes.
 *
 * ── A CORREÇÃO DE HIERARQUIA ──────────────────────────────────────────────
 * Eram QUATRO bandas empilhadas antes do primeiro campo: trilha, faixa da
 * empresa, título e um separador. Todas do mesmo peso e quase da mesma cor, o
 * que dava três telas de rolagem de introdução antes do trabalho começar e
 * nenhuma pista de qual delas importava. Agora são DUAS células: onde estou e
 * o que faço aqui. A faixa da empresa entrou no cabeçalho como contexto à
 * direita e o separador sumiu — quem separa duas células é a parede de cera,
 * e um traço entre elas era dizer a mesma coisa duas vezes. */
export function Pagina({
  titulo,
  descricao,
  etapa,
  acoes,
  semIdentidade = false,
  children,
}: {
  titulo: string;
  descricao?: string | undefined;
  /** Quando informada, desenha a trilha do funil e a identidade da empresa. */
  etapa?: IdEtapa | undefined;
  /**
   * Desliga a identidade da empresa no cabeçalho.
   *
   * Duas telas do funil já respondem "de quem são estes números" melhor do que
   * a faixa: a de EMPRESA, cujo formulário logo abaixo é a própria empresa, e
   * a de PREÇO, que tem uma faixa de contexto própria com empresa, projeto em
   * análise e o que falta. Nas duas, repetir o nome ao lado do título faria a
   * tela dizer duas vezes a mesma coisa antes do primeiro dado.
   */
  semIdentidade?: boolean | undefined;
  acoes?: React.ReactNode;
  children: React.ReactNode;
}) {
  const indice = etapa === undefined ? -1 : ETAPAS.findIndex((e) => e.id === etapa);
  const daEtapa = indice === -1 ? undefined : ETAPAS[indice];

  // O casco vem da LISTA, não da tela: quem decide se a etapa é formulário
  // (marca à esquerda, coluna com medida própria) ou painel (largura cheia) é
  // `src/etapas.ts`. Tela fora do funil é painel, porque relatório e histórico
  // são documento e tabela.
  const marca = daEtapa?.casco === 'formulario' ? 'lateral' : 'topo';

  return (
    <Wrap marca={marca}>
      <div className="favo">
        {etapa ? <Passos atual={etapa} /> : null}

        <Celula como="header" respiro="folgado" className="grid gap-3">
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
            <div className="grid min-w-0 gap-1.5">
              {daEtapa ? (
                <p className="rotulo">
                  {daEtapa.grupo === 'configuracao' ? 'Configuração' : 'Precificação'}
                  {' · '}
                  Etapa {indice + 1} de {ETAPAS.length}
                </p>
              ) : null}
              <h1 className="titulo-tela">{titulo}</h1>
            </div>

            {etapa && !semIdentidade ? <FaixaEmpresa className="shrink-0" /> : null}
          </div>

          {descricao || acoes ? (
            <div
              className={cn(
                'flex flex-wrap items-center justify-between gap-3',
                // A divisória só existe para separar a descrição das AÇÕES.
                // Sem ações ela vira um traço decorativo entre um título e a
                // frase que explica o título, isto é, entre duas coisas que
                // são a mesma coisa.
                acoes && 'border-fio border-t pt-4',
              )}
            >
              {descricao ? (
                <p className="apoio">{descricao}</p>
              ) : (
                <span />
              )}
              {acoes ? <div className="flex shrink-0 items-center gap-2">{acoes}</div> : null}
            </div>
          ) : null}
        </Celula>

        {children}
      </div>
    </Wrap>
  );
}
